import { useState, useEffect, createRef } from 'react'
import { Form, Field, FormElement, FormRenderProps } from '@progress/kendo-react-form'
import {
    FormDropDownList,
    FormInput,
    FormAddressLookup,
    FormSwitch,
    FormNumericTextBox,
} from '../FormComponents'
import { dropDown, fileDocumentTypes } from '../../../data/interfaces'
import SkeletonForm from '../../Skeletons/SkeletonForm'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { useToast } from '../../../contexts/ToastContext'
import { useNavigate } from 'react-router-dom'
import { createAccount, createAccountAddress, createAccountContact, deleteAccountAddress, deleteAccountContact, editAccountAddress, editAccountContact, GetClientByIdV2, getClientByIdV2, getTitlesForDropdown, updateAccount, getClientXeroBalance, XeroBalanceResponse } from '../../../data/accountsData'
import { FullAddressResponse } from '../../../types/addressLookup.types'
import { getFullAddress, searchAddresses } from '../../../helpers/addressLookup'
import { FaPlusCircle, FaTrash } from 'react-icons/fa'
import Divider from '../../Layout/Divider'
import { FaFileAlt, FaFileCsv, FaFileImage, FaFilePdf, } from 'react-icons/fa';
import { ExternalDropZone, Upload, UploadFileInfo, UploadListItemProps, UploadOnStatusChangeEvent } from '@progress/kendo-react-upload'
import { deleteClient, getClientDocuments } from '../../../data/clientData'
import { deleteDocument } from '../../../data/fileData'
import QuoteDocumentRow from '../../QuoteDocumentRow'
import './AccountForm.css'

interface Props {
    id?: number | null
    mode: "edit" | "add"
    handleExpanded?: (fn?: () => void) => void
    getTableData?: () => void
    setAccountId?: (id: number) => void
    flushBottom?: boolean
    phoneNumber?: string
    setClientId?: (id: number) => void
    incomingLayout?: boolean
    onClose?: (visible: boolean) => void
    initialTab?: number
    /** Accounts.View on the Accounts page: look, don't change. */
    readOnly?: boolean
}

const AccountForm = (props: Props) => {
    // ...existing code...

    // Reliable notes save handler
    const handleSaveNotes = async () => {
        try {
            const payload = {
                ...formState,
                notes: formState.notes,
                clientName: formState.clientName || `${formState.primaryContact?.firstname ?? ''} ${formState.primaryContact?.lastname ?? ''}`.trim(),
                clientLetterUrl: createClientLetter ? 'awaiting letter generation' : formState.clientLetterUrl
            };
            await updateAccount(payload);
            showToast('Notes updated', 'success');
        } catch (err) {
            showToast('Failed to update notes', 'error');
        }
    };
    /** The selected item's full details */
    const [selectedItem, setSelectedItem] = useState<GetClientByIdV2 | undefined>(undefined);
    /** Xero balance information */
    const [xeroBalance, setXeroBalance] = useState<XeroBalanceResponse | null>(null);
    /** Are we loading the selected item from the API? */
    const [loading, setLoading] = useState(false);
    /** The selected tab in the sidebar */
    const [selectedTab, setSelectedTab] = useState<number>(props.initialTab || 0);

    /** A dialog boolean for wether the complete dialog should be visible */
    const [completeDialogVisible, setCompleteDialogVisible] = useState(false);

    /** Toast hook to display toast */
    const { showToast } = useToast()

    const uploadRef = createRef<Upload>();

    /** Store all the addresses from the search term */
    const [addresses, setAddresses] = useState<dropDown[]>([]);
    /** When doing an address lookup, store the clicked address here */
    const [selectedAddress, setSelectedAddress] = useState<FullAddressResponse | undefined>(undefined);

    const [selectedAddressDrodownItem, setSelectedAddressDropdownItem] = useState<dropDown[]>([]);

    const [titles, setTitles] = useState<dropDown[]>([]);

    const [mode, setMode] = useState<'edit' | 'add'>(props.mode);


    const navigate = useNavigate();
    const [files, setFiles] = useState<UploadFileInfo[]>([])
    const token = localStorage.getItem('authToken');
    const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([
        {
            documentId: 0,
            fileName: '',
            fileExtension: '',
            appCanView: false,
        },
    ]);
    const [createClientLetter, setCreateClientLetter] = useState(false);
    const apiURL = process.env.REACT_APP_API_URL;


    const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
        setFiles(event.newState)
        showToast('Succesfully uploaded new document', 'success')
    }


    /** A blank from state */
    const blankFormState: GetClientByIdV2 = {
        clientId: 0,
        sites: [],
        contacts: [],
        email: "",
        phone: "",
        accountRef: "",
        clientAddr: "",
        clientName: "",
        contactFirstName: "",
        contactLastName: "",
        contactName: "",
        deletedDate: "",
        directdebitEmail: "",
        email2: "",
        email3: "",
        fax: "",
        openEnquiries: 0,
        openJobs: 0,
        openQuotes: 0,
        phone2: "",
        postcode: "",
        tradeContact: "",
        vatNum: "",
        websiteUrl: "",
        what3Words: "",
        notes: "",
        badDebter: false,
        defaultAdjustment: 0,
        // regNumber: "",
        xeroBalance: null,
        clientLetterUrl: null,
        // reference: "",
        // vatNumber: "",
        // website: "",
        primaryContact: {
            clientId: 0,
            contactId: 0,
            fullName: '',
            email: "",
            firstname: "",
            isPrimary: true,
            lastname: "",
            phone: props.phoneNumber || "",
            title: "",
            titleId: 0
        },
        primarySite: {
            siteAddr: '',
            what3Words: '',
            deletedDate: '',
            siteId: 0,
            buildingName: "",
            buildingNumber: "",
            country: "",
            county: "",
            district: "",
            isBilling: false,
            isDelivery: false,
            latitude: "",
            line1: "",
            line2: "",
            line3: "",
            line4: "",
            locality: "",
            longitude: "",
            postcode: "",
            residential: false,
            subBuildingName: "",
            subBuildingNumber: "",
            thoroughfare: "",
            townOrCity: ""
        }
    }
    /** Form state */
    const [formState, setFormState] = useState<GetClientByIdV2>(blankFormState);

    /**
     * Fetches titles for the dropdown and stores them in the state.
     * 
     * @async
     * @function getAndStoreTitles
     * @returns {Promise<void>} A promise that resolves when the titles are fetched and stored.
     * @throws Will log an error to the console if the fetch operation fails.
     */
    const getAndStoreTitles = async () => {
        try {
            const res = await getTitlesForDropdown();
            setTitles(res);
        } catch (err) {
            console.error(err)
        }
    }

    /**
     * Retrieves the selected service reminder item by its ID and sets it as the selected item.
     * @returns {Promise<void>} A promise that resolves when the selected item is retrieved and set.
     */
    const getSelectedItem = async () => {
        if (props.mode === 'edit') {
            setLoading(true)
            if (props.id) {
                const item = await getClientByIdV2(props.id)
                setSelectedItem(item);
                setFormState((prev) => ({
                    ...prev,
                    ...item
                    // clientId: item.clientId,
                    // email: item.email,
                    // phone: item.phone,
                    // websiteUrl: item.websiteUrl,
                    // sites: item.sites,
                    // contacts: item.contacts,
                    // primarySite: item.primarySite,
                    // primaryContact: item.primaryContact
                }));
            }
            setLoading(false)
        }
    }

    const clientNameFormatter = () => {
        if (formState.primarySite.residential) {
            return `${formState.primaryContact.firstname}${" "}${formState.primaryContact.lastname}`
        } else {
            return formState.primarySite.line1
        }
    }

    /**
     * Handles the click event when the save button is clicked.
     * Updates the service reminder with the new form state values.
     */
    const saveClickHandler = async () => {
        if (selectedItem !== undefined) {
            const formStateToSave = {
                ...formState,
                clientName: clientNameFormatter(),
                clientLetterUrl: createClientLetter ? 'awaiting letter generation' : formState.clientLetterUrl
            };
            updateAccount(formStateToSave)
                .then(() => {
                    if (props.getTableData) {
                        props.getTableData();
                    }
                    if(props.onClose){
                        props.onClose(false)
                    }
                    showToast('Account updated', 'success')
                })
                .catch((error) => {
                    console.error(error)
                    showToast('Failed to update account', 'error')
                })
        }
    }

    const deleteClickHandler = async () => {
        if (selectedItem === undefined) return;
        try {
            const res = await deleteClient(selectedItem.clientId)
            console.log(res)
            showToast('Account deleted', 'success')
            if (props.getTableData) {
                props.getTableData();
            }
        } catch (err) {
            showToast('Failed to delete account', 'error')
            console.log(err)
        }
        // deleteServiceReminder(selectedItem.serviceReminderId)
        //     .then(() => {
        //         props.getTableData()
        //     })
        //     .catch((error) => {
        //         console.error(error)
        //     })
    }



    /**
     * Handles the click event to create a new account.
     * 
     * This function attempts to create a new account using the provided form state.
     * If the account creation is successful, it logs the response, refreshes the table data,
     * and shows a success toast message. If the account creation fails, it logs the error
     * and shows an error toast message.
     * 
     * @returns {Promise<void>} A promise that resolves when the account creation process is complete.
     */
    const createClickHandler = async (): Promise<void> => {
        try {
            const formStateToCreate = {
                ...formState,
                clientName: clientNameFormatter(),
                primarySite: {
                    ...formState.primarySite,
                    latitude: formState.primarySite.latitude || "0",
                    longitude: formState.primarySite.longitude || "0"
                }
            };
            const res = await createAccount(formStateToCreate);
            if (props.setClientId) {
                props.setClientId(res.data.clientId)
            }
            setMode('edit');
            if (props.getTableData) {
                props.getTableData();
            }
            if (props.setAccountId) {
                props.setAccountId(res.data.clientId)
            }
            showToast('Account created', 'success')
        } catch (error) {
            console.log(error)
            showToast('Failed to create account', 'error')
        }
    }


    /**
     * Checks if the form is valid. Disabled the save button if the form is invalid.
     * @returns {boolean} True if the form is valid, false otherwise.
     */
    const isFormValid = (): boolean => {
        const { primaryContact, primarySite } = formState;
        return (
            !primaryContact ||
            !primarySite ||
            primaryContact?.titleId === 0 ||
            (primaryContact?.firstname?.trim() ?? "") === "" ||
            (primaryContact?.lastname?.trim() ?? "") === "" ||
            (primaryContact?.email?.trim() ?? "") === "" ||
            (primaryContact?.phone?.trim() ?? "") === "" ||
            (primarySite?.line1?.trim() ?? "") === "" ||
            (primarySite?.postcode?.trim() ?? "") === ""
        );
    }


    /**
     * Get the selected item on load, if there's and ID and the mode is edit
     */
    useEffect(() => {
        getSelectedItem()
    }, [props.id, props.mode]);

    /**
     * Fetch Xero balance when in edit mode and clientId is available
     */
    useEffect(() => {
        const fetchXeroBalance = async () => {
            if (props.mode === 'edit' && formState.clientId && !props.incomingLayout) {
                const balance = await getClientXeroBalance(formState.clientId);
                setXeroBalance(balance);
            }
        };
        fetchXeroBalance();
    }, [props.mode, formState.clientId]);

    /**
     * If we are adding a new account, clear the state
     */
    useEffect(() => {
        if (props.mode === 'add') {
            setFormState(() => ({
                ...blankFormState,
            }));
        }
    }, [props.mode]);

    const accountStyle = {
        padding: "20px",
        border: "1px solid #ccc",
    };

    const accountContainerStyle = {
        display: "flex",
        flexFlow: "column",
        gap: "20px"
    }

    const titleStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    }

    /**
     * Adds an additional address to the current form state.
     * 
     * This function creates a new address object with default values and 
     * appends it to the `addresses` array in the form state.
     * 
     */
    const addAdditionalAddressHandler = async () => {
        const currentFormState = formState;
        currentFormState.sites.push({
            siteId: Math.floor(Math.random() * 1000),
            clientId: formState.clientId,
            line1: "",
            line2: "",
            line3: "",
            line4: "",
            country: "",
            postcode: "",
            county: "",
            district: "",
            latitude: "",
            locality: "",
            longitude: "",
            residential: false,
            buildingName: "",
            buildingNumber: "",
            subBuildingName: "",
            subBuildingNumber: "",
            thoroughfare: "",
            townOrCity: "",
            isBilling: false,
            isDelivery: false,
            isNewFromClient: true,
            deletedDate: "",
            what3Words: "",
        });
        setFormState({ ...currentFormState });
    };

    /**
     * Adds a new address to the account if the provided address ID matches an address in the form state.
     * 
     * @param {number | undefined} siteId - The ID of the address to be added.
     * @returns {Promise<void>} A promise that resolves when the address is successfully added or rejects with an error.
     * 
     * @throws Will show a toast notification with an error message if the address could not be added.
     */
    const addNewAddressesToAccount = async (siteId: number | undefined) => {
        if (siteId) {
            const matched = formState.sites.find(addr => addr.siteId === siteId);
            if (matched) {
                try {
                    const addressToCreate = {
                        ...matched,
                        latitude: matched.latitude || "0",
                        longitude: matched.longitude || "0"
                    };
                    await createAccountAddress(addressToCreate);
                    showToast('Address added', 'success')
                    getSelectedItem()
                    if(props.onClose){
                        props.onClose(false)
                    }
                } catch (err) {
                    showToast('Failed to add address', 'error')
                }
            }
        }
    }

    /**
     * Edits an existing address by its ID.
     * 
     * This function takes an address ID, finds the corresponding address in the form state,
     * and attempts to edit it using the `editAccountAddress` function. If the address is found
     * and the edit operation is successful, the response is logged to the console. If an error
     * occurs during the edit operation, it is caught and logged to the console.
     * 
     * @param {number | undefined} siteId - The ID of the address to be edited. If undefined, the function does nothing.
     * @returns {Promise<void>} A promise that resolves when the edit operation is complete.
     */
    const editExistingAddress = async (siteId: number | undefined) => {
        if (siteId) {
            const matched = formState.sites.find(addr => addr.siteId === siteId);
            if (matched) {
                try {
                    await editAccountAddress(matched);
                    showToast('Address updated', 'success')
                    getSelectedItem()
                } catch (err) {
                    showToast('Failed to update address', 'error')
                    console.error(err)
                }
            }
        }
    }

    /**
     * Handler function to add an additional contact to the form state.
     * This function updates the form state by pushing a new contact object
     * with default values into the contacts array.
     */
    const addAdditionalContactHandler = () => {
        const currentFormState = formState;
        currentFormState.contacts.push({
            clientId: formState.clientId,
            contactId: Math.floor(Math.random() * 1000),
            email: "",
            firstname: "",
            lastname: "",
            phone: "",
            isPrimary: false,
            isNewFromClient: true,
            title: "",
            titleId: 0,
            fullName: ""
        });
        setFormState({ ...currentFormState });
    }

    /**
     * Handles the deletion of an account address.
     * 
     * @param {number | undefined} siteId - The ID of the address to delete. If undefined, the function will not proceed.
     * @returns {Promise<void>} - A promise that resolves when the address is deleted or rejects if an error occurs.
     * @throws Will show a toast message with 'Failed to delete address' if the deletion fails.
     */
    const deleteAddressHandler = async (siteId: number | undefined) => {
        if (siteId) {
            try {
                await deleteAccountAddress(siteId);
                showToast('Address deleted', 'success')
            } catch (err) {
                showToast('Failed to delete address', 'error')
            }
        }
    }

    /**
     * Adds a new contact to the account.
     *
     * @param {number | undefined} contactId - The ID of the contact to be added. If undefined, the function does nothing.
     * @returns {Promise<void>} A promise that resolves when the contact is added or rejects if there is an error.
     */
    const addNewContact = async (contactId: number | undefined) => {
        if (contactId) {
            const matched = formState.contacts.find(cont => cont.contactId === contactId);
            if (matched) {
                try {
                    const { title, ...contactWithoutTitle } = matched;
                    await createAccountContact(contactWithoutTitle);
                    showToast('Contact added', 'success')
                    getSelectedItem();
                    if(props.onClose){
                        props.onClose(false)
                    }
                } catch (err) {
                    showToast('Failed to add contact', 'error')
                }
            }
        }
    }

    /**
     * Edits an existing contact by its ID.
     * 
     * This function finds the contact in the form state by the given contact ID,
     * and if found, attempts to update the contact using the `editAccountContact` function.
     * If the update is successful, it refreshes the selected item and shows a success toast message.
     * If the update fails, it shows an error toast message.
     * 
     * @param contactId - The ID of the contact to be edited. It can be a number or undefined.
     * @returns A promise that resolves when the contact update process is complete.
     */
    const editExistingContact = async (contactId: number | undefined) => {
        if (contactId) {
            const matched = formState.contacts.find(cont => cont.contactId === contactId);
            if (matched) {
                try {
                    const { title, ...contactWithoutTitle } = matched;
                    await editAccountContact(contactWithoutTitle);
                    getSelectedItem();
                    showToast('Contact updated', 'success')
                } catch (err) {
                    showToast('Failed to update contact', 'error')
                }
            }
        }
    }

    /**
     * Handles the deletion of a contact by its ID.
     * 
     * @param contactId - The ID of the contact to be deleted. It can be a number or undefined.
     * @returns A promise that resolves when the contact is deleted.
     * 
     * @remarks
     * If the contactId is defined, it attempts to delete the contact using the `deleteAccountContact` function.
     * On successful deletion, it shows a success toast message.
     * If an error occurs during deletion, it shows an error toast message.
     */
    const deleteContactHandler = async (contactId: number | undefined) => {
        if (contactId) {
            try {
                await deleteAccountContact(contactId);
                showToast('Contact deleted', 'success')
            } catch (err) {
                showToast('Failed to delete contact', 'error')
            }
        }
    }

    /**
     * Handles the form submission based on the mode (edit or add).
     * 
     * - If the mode is 'edit', it calls the `saveClickHandler` function and then
     *   optionally calls the `handleExpanded` function if it is provided.
     * - If the mode is 'add', it calls the `createClickHandler` function and then
     *   optionally calls the `handleExpanded` function if it is provided.
     * 
     * @returns {void}
     */
    const onSubmitHandler = () => {
        if (props.mode === 'edit') {
            console.log("running save click")
            saveClickHandler()
            if (props.handleExpanded) {
                props.handleExpanded()
            }
        }

        if (props.mode === 'add') {
            console.log("running add click")
            createClickHandler()
            if (props.handleExpanded) {
                props.handleExpanded()
            }
        }
    }

    const resetAddressState = () => {
        setSelectedAddress(undefined);
        setAddresses([]);
        setSelectedAddressDropdownItem([]);
    }

    useEffect(() => {
        getAndStoreTitles();
        resetAddressState()
    }, []);

    useEffect(() => {
        if (props.mode === "add") {
            setFormState(blankFormState)
        }
    }, [props.mode])

    const icon = (fileName: string) => {
        if (fileName.includes('.pdf')) {
            return (
                <FaFilePdf
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        } else if (fileName.includes('.csv')) {
            return (
                <FaFileCsv
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        } else if (fileName.includes('.txt')) {
            return (
                <FaFileAlt
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        } else if (fileName.includes('.jpg')) {
            return (
                <FaFileImage
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        } else if (fileName.includes('.png')) {
            return (
                <FaFileImage
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        } else {
            return (
                <FaFileAlt
                    style={{
                        fontSize: '20pt',
                        color: 'rgb(33, 67, 59)',
                        marginRight: '1rem',
                    }}
                />
            )
        }
    }


    const CustomListItemUI = (props: UploadListItemProps) => {
        return (
            <ul>
                {props.files.map((file) => (
                    <div
                        style={{ fontSize: '10pt', fontFamily: 'Heebo, Sans-Serif' }}
                        key={file.name}
                    >
                        {icon(file.name)}
                        {file.name}
                    </div>
                ))}
            </ul>
        )
    }

    useEffect(() => {
        const fetchDocumentData = async () => {
            const documentData = await getClientDocuments(formState.clientId)
            setDocumentData(documentData)
        }

        fetchDocumentData()
    }, [files, formState.clientId])

    const handleDocumentDelete = async (documentId: number) => {
        await deleteDocument(documentId)
        setDocumentData(
            documentData.filter((document: any) => document.documentId !== documentId)
        )
    }

    console.log(formState)

    const renderNormalLayout = (formRenderProps: FormRenderProps): React.ReactElement => {
        return (
            <fieldset className={'k-form-fieldset'} disabled={props.readOnly}>
                {props.readOnly && <p className='k-form-hint'>You have view-only access to accounts.</p>}
                {
                    props.mode === "edit" && (
                        <Field
                            name={'reference'}
                            component={FormInput}
                            label={'Reference:'}
                            disabled={true}
                            defaultValue={formState.accountRef}
                            onChange={(e) => {
                                setFormState({ ...formState, accountRef: e.value })
                            }}
                        />
                    )
                }
                <Divider>Primary Address</Divider>
                {
                    props.mode === "edit" && (
                        formState.primarySite === null ? (
                            <span style={{ color: "#e9530d" }}>{formState.clientAddr} {formState.postcode}</span>
                        ) : null
                    )
                }
                <Field
                    data={addresses}
                    name={'addressLookup'}
                    setData={setAddresses}
                    component={FormAddressLookup}
                    label={'Find address:'}
                    required={true}
                    defaultItem={selectedAddressDrodownItem}
                    selectedAddress={formState.primarySite || selectedAddress}
                    searchDropdown={(term: string) => searchAddresses(term, setAddresses)}
                    setSelectedAddress={(updatedAddress: any) => {
                        // Update the primary address in formState.addresses
                        setFormState((prevState) => ({
                            ...prevState,
                            primarySite: updatedAddress
                        }));
                    }}
                    onChange={async (e) => {
                        // If value is null (user started typing), clear the selection
                        if (!e.value) {
                            setSelectedAddressDropdownItem([]);
                            return;
                        }
                        
                        setSelectedAddressDropdownItem(e.value)
                        const address = await getFullAddress(e.value.data);
                        setFormState((prevState) => ({
                            ...prevState,
                            primarySite: {
                                ...prevState.primarySite,
                                clientId: formState.clientId,
                                buildingName: address.buildingName,
                                buildingNumber: address.buildingNumber,
                                country: address.country,
                                county: address.county,
                                district: address.district,
                                isBilling: true,
                                isDelivery: true,
                                isPrimary: true,
                                latitude: address.latitude || "0",
                                line1: address.line1,
                                line2: address.line2,
                                line3: address.line3,
                                line4: address.line4,
                                locality: address.locality,
                                longitude: address.longitude || "0",
                                postcode: address.postcode,
                                residential: address.residential,
                                subBuildingName: address.subBuildingName,
                                subBuildingNumber: address.subBuildingNumber,
                                thoroughfare: address.thoroughfare,
                                townOrCity: address.townOrCity,
                            }
                        }));
                        setSelectedAddress(address);
                    }}
                />
                <Divider>Primary Contact</Divider>

                {/* <Field
                name={'name'}
                required={true}
                component={FormInput}
                label={'Name:'}
                defaultValue={formState.name}
                onChange={(e) => {
                    setFormState({ ...formState, name: e.value })
                }}
            /> */}
                <Field
                    data={titles}
                    name={'title'}
                    component={FormDropDownList}
                    label={'Title *:'}
                    required
                    defaultItem={formState.primaryContact ? { text: formState.primaryContact.title, value: formState.primaryContact.titleId } : { text: '', value: 0 }}
                    onChange={(e) => {
                        setFormState(prev => ({
                            ...prev,
                            primaryContact: {
                                ...prev.primaryContact,
                                titleId: e.value.value
                            }
                        }))
                    }}
                />
                <Field
                    name={'firstName'}
                    required={true}
                    component={FormInput}
                    label={'First Name *:'}
                    defaultValue={formState.primaryContact?.firstname}
                    onChange={(e) => {
                        setFormState(prev => ({
                            ...prev,
                            contactFirstName: e.value,
                            primaryContact: {
                                ...prev.primaryContact,
                                firstname: e.value
                            }
                        }))
                    }}
                />
                <Field
                    name={'lastName'}
                    required={true}
                    component={FormInput}
                    label={'Last Name *:'}
                    defaultValue={formState.primaryContact?.lastname}
                    onChange={(e) => {
                        setFormState(prev => ({
                            ...prev,
                            contactLastName: e.value,
                            primaryContact: {
                                ...prev.primaryContact,
                                lastname: e.value
                            }
                        }))
                    }}
                />
                <Field
                    name={'email'}
                    required={true}
                    component={FormInput}
                    type={'email'}
                    label={'Email *:'}
                    defaultValue={formState.primaryContact?.email}
                    onChange={(e) => {
                        setFormState(prev => ({
                            ...prev,
                            primaryContact: {
                                ...prev.primaryContact,
                                email: e.value
                            }
                        }))
                    }}
                />
                <Field
                    name={'phone'}
                    required={true}
                    component={FormInput}
                    label={'Phone *:'}
                    defaultValue={formState.primaryContact?.phone}
                    onChange={(e) => {
                        setFormState(prev => ({
                            ...prev,
                            primaryContact: {
                                ...prev.primaryContact,
                                phone: e.value
                            }
                        }))
                    }}
                />
                <Field
                    name={'website'}
                    component={FormInput}
                    label={'Website:'}
                    defaultValue={formState.websiteUrl}
                    onChange={(e) => {
                        setFormState({ ...formState, websiteUrl: e.value })
                    }}
                />
                <Divider>Account</Divider>
                {props.mode === 'edit' && xeroBalance && (
                    <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Current Balance:</label>
                        {xeroBalance.success ? (
                            <div style={{ 
                                padding: '10px', 
                                backgroundColor: '#f8f9fa', 
                                border: '1px solid #dee2e6', 
                                borderRadius: '4px'
                            }}>
                                <div><strong>{xeroBalance.clientName}</strong></div>
                                <div style={{ marginTop: '8px' }}>
                                    <div>Accounts Receivable Outstanding: <strong>£{xeroBalance.accountsReceivableOutstanding?.toFixed(2)}</strong></div>
                                    <div>Accounts Receivable Overdue: <strong>£{xeroBalance.accountsReceivableOverdue?.toFixed(2)}</strong></div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ 
                                padding: '10px', 
                                backgroundColor: '#fff3cd', 
                                border: '1px solid #ffc107', 
                                borderRadius: '4px',
                                color: '#856404'
                            }}>
                                {xeroBalance.message}
                            </div>
                        )}
                    </div>
                )}
                <Field
                    name={'badDebter'}
                    component={FormSwitch}
                    label={'Bad Debtor:'}
                    checked={formState.badDebter}
                    onChange={(e) => {
                        setFormState({ ...formState, badDebter: e.value })
                    }}
                />
                <Field
                    name={'defaultAdjustment'}
                    component={FormNumericTextBox}
                    label={'Default Adjustment (%):'}
                    hint={'Applied to the Adjustment (%) on the workings tab of new jobs for this client. Leave at zero for none.'}
                    format={'n2'}
                    step={0.5}
                    value={formState.defaultAdjustment}
                    onChange={(e) => {
                        setFormState({ ...formState, defaultAdjustment: e.value ?? 0 })
                    }}
                />
                <Divider>Other</Divider>
                {/* <Field
                name={'regNumber'}
                component={FormInput}
                label={'Reg No.:'}
                defaultValue={formState.}
                onChange={(e) => {
                    setFormState({ ...formState, regNumber: e.value })
                }}
            /> */}
                {props.mode === 'edit' && formState.clientLetterUrl === null && (
                    <Field
                        name={'createClientLetter'}
                        component={FormSwitch}
                        label={'Create client letter'}
                        defaultValue={createClientLetter}
                        onChange={(e) => {
                            setCreateClientLetter(e.value)
                        }}
                    />
                )}
                {props.mode === 'edit' && formState.clientLetterUrl && formState.clientLetterUrl.startsWith('https') && (
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Letter:</label>
                        <a href={formState.clientLetterUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1e2a4e', textDecoration: 'underline' }}>
                            View Client Letter
                        </a>
                    </div>
                )}
                {props.mode === 'edit' && formState.clientLetterUrl && !formState.clientLetterUrl.startsWith('https') && (
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Letter:</label>
                        <span style={{ color: '#666' }}>Client letter being generated</span>
                    </div>
                )}
                <Field
                    name={'vatNumber'}
                    component={FormInput}
                    label={'VAT No.:'}
                    defaultValue={formState.vatNum}
                    onChange={(e) => {
                        setFormState({ ...formState, vatNum: e.value })
                    }}
                />

                {!props.readOnly && (
                <div className='button__container'>
                    {
                        props.mode === 'edit' && (
                            <button
                                onClick={() => onSubmitHandler()}
                                disabled={isFormValid()}
                                className='form__edit__button accept'
                            >
                                Save changes
                            </button>
                        )
                    }

                    {
                        props.mode === 'edit' && (
                            <button
                                onClick={() => {
                                    deleteClickHandler()
                                    formRenderProps.onFormReset()
                                    if (props.handleExpanded) {
                                        props.handleExpanded()
                                    }
                                }}
                                className='form__edit__button reject'
                            >
                                Delete account
                            </button>
                        )
                    }

                    {
                        props.mode === "add" && (
                            <button
                                disabled={isFormValid()}
                                onClick={() => onSubmitHandler()}
                                className='form__edit__button accept'
                            >
                                Add Account
                            </button>
                        )
                    }
                </div>
                )}

            </fieldset>
        )
    }

    const renderIncomingLayout = (formRenderProps: FormRenderProps): React.ReactElement => {
        return (
            <fieldset className={'k-form-fieldset'}>
                {
                    props.mode === "edit" && (
                        formState.primarySite === null ? (
                            <span style={{ color: "#e9530d" }}>{formState.clientAddr} {formState.postcode}</span>
                        ) : null
                    )
                }
                <div className={'incoming-grid'}>

                    <Field
                        data={titles}
                        name={'title'}
                        component={FormDropDownList}
                        label={'Title *:'}
                        required
                        defaultItem={formState.primaryContact ? { text: formState.primaryContact.title, value: formState.primaryContact.titleId } : { text: '', value: 0 }}
                        onChange={(e) => {
                            setFormState(prev => ({
                                ...prev,
                                primaryContact: {
                                    ...prev.primaryContact,
                                    titleId: e.value.value
                                }
                            }))
                        }}
                    />
                    <Field
                        name={'firstName'}
                        required={true}
                        component={FormInput}
                        label={'First Name *:'}
                        defaultValue={formState.primaryContact?.firstname}
                        onChange={(e) => {
                            setFormState(prev => ({
                                ...prev,
                                contactFirstName: e.value,
                                primaryContact: {
                                    ...prev.primaryContact,
                                    firstname: e.value
                                }
                            }))
                        }}
                    />
                    <Field
                        name={'lastName'}
                        required={true}
                        component={FormInput}
                        label={'Last Name *:'}
                        defaultValue={formState.primaryContact?.lastname}
                        onChange={(e) => {
                            setFormState(prev => ({
                                ...prev,
                                contactLastName: e.value,
                                primaryContact: {
                                    ...prev.primaryContact,
                                    lastname: e.value
                                }
                            }))
                        }}
                    />
                    {
                        props.mode === "edit" ? (
                            <Field
                                name={'reference'}
                                component={FormInput}
                                label={'Reference:'}
                                disabled={true}
                                defaultValue={formState.accountRef}
                                onChange={(e) => {
                                    setFormState({ ...formState, accountRef: e.value })
                                }}
                            />
                        ) : null
                    }
                    <Field
                        name={'email'}
                        required={true}
                        component={FormInput}
                        type={'email'}
                        label={'Email *:'}
                        defaultValue={formState.primaryContact?.email}
                        onChange={(e) => {
                            setFormState(prev => ({
                                ...prev,
                                primaryContact: {
                                    ...prev.primaryContact,
                                    email: e.value
                                }
                            }))
                        }}
                    />
                    <Field
                        name={'phone'}
                        required={true}
                        component={FormInput}
                        label={'Phone *:'}
                        defaultValue={formState.primaryContact?.phone}
                        onChange={(e) => {
                            setFormState(prev => ({
                                ...prev,
                                primaryContact: {
                                    ...prev.primaryContact,
                                    phone: e.value
                                }
                            }))
                        }}
                    />
                </div>
                <Field
                    data={addresses}
                    name={'addressLookup'}
                    setData={setAddresses}
                    component={FormAddressLookup}
                    label={'Find address:'}
                    gridLayout={true}
                    required={true}
                    defaultItem={selectedAddressDrodownItem}
                    selectedAddress={formState.primarySite || selectedAddress}
                    searchDropdown={(term: string) => searchAddresses(term, setAddresses)}
                    setSelectedAddress={(updatedAddress: any) => {
                        // Update the primary address in formState.addresses
                        setFormState((prevState) => ({
                            ...prevState,
                            primarySite: updatedAddress
                        }));
                    }}
                    onChange={async (e) => {
                        // If value is null (user started typing), clear the selection
                        if (!e.value) {
                            setSelectedAddressDropdownItem([]);
                            return;
                        }
                        
                        setSelectedAddressDropdownItem(e.value)
                        const address = await getFullAddress(e.value.data);
                        setFormState((prevState) => ({
                            ...prevState,
                            primarySite: {
                                ...prevState.primarySite,
                                clientId: formState.clientId,
                                buildingName: address.buildingName,
                                buildingNumber: address.buildingNumber,
                                country: address.country,
                                county: address.county,
                                district: address.district,
                                isBilling: true,
                                isDelivery: true,
                                isPrimary: true,
                                latitude: address.latitude || "0",
                                line1: address.line1,
                                line2: address.line2,
                                line3: address.line3,
                                line4: address.line4,
                                locality: address.locality,
                                longitude: address.longitude || "0",
                                postcode: address.postcode,
                                residential: address.residential,
                                subBuildingName: address.subBuildingName,
                                subBuildingNumber: address.subBuildingNumber,
                                thoroughfare: address.thoroughfare,
                                townOrCity: address.townOrCity,
                            }
                        }));
                        setSelectedAddress(address);
                    }}
                />

                <div className={'incoming-grid'}>



                    <Field
                        name={'website'}
                        component={FormInput}
                        label={'Website:'}
                        defaultValue={formState.websiteUrl}
                        onChange={(e) => {
                            setFormState({ ...formState, websiteUrl: e.value })
                        }}
                    />

                    <Field
                        name={'vatNumber'}
                        component={FormInput}
                        label={'VAT No.:'}
                        defaultValue={formState.vatNum}
                        onChange={(e) => {
                            setFormState({ ...formState, vatNum: e.value })
                        }}
                    />
                </div>

                <div className='button__container'>
                    {
                        props.mode === 'edit' && (
                            <button
                                onClick={() => onSubmitHandler()}
                                disabled={isFormValid()}
                                className='form__edit__button accept'
                            >
                                Save changes
                            </button>
                        )
                    }

                    {
                        props.mode === 'edit' && (
                            <button
                                onClick={() => {
                                    deleteClickHandler()
                                    formRenderProps.onFormReset()
                                    if (props.handleExpanded) {
                                        props.handleExpanded()
                                    }
                                }}
                                className='form__edit__button reject'
                            >
                                Delete account
                            </button>
                        )
                    }

                    {
                        props.mode === "add" && (
                            <button
                                onClick={() => onSubmitHandler()}
                                disabled={isFormValid()}
                                className='form__edit__button accept'
                            >
                                Add Account
                            </button>
                        )
                    }
                </div>
            </fieldset>
        )
    }

    return (
        <>
            {
                loading && selectedItem === undefined ? (
                    <SkeletonForm />
                ) : (
                    <div className='form__wrapper'>
                        <div className='form__tabstrip__outer'>
                            <TabStrip
                                animation={false}
                                selected={selectedTab}
                                onSelect={(e) => setSelectedTab(e.selected)}
                                className='form__tabstrip__container'
                            >
                                <TabStripTab title="Account Information">
                                    <Form
                                        // initialValues={{ description: formState.description }}
                                        render={(formRenderProps) => (
                                            <FormElement style={{ background: "white", borderRadius: "10px", padding: props.incomingLayout ? '0' : '30px 1rem 1rem 1rem', width: '100%', marginBottom: props.flushBottom ? '0' : '5rem' }} >
                                                {
                                                    props.incomingLayout ? (
                                                        renderIncomingLayout(formRenderProps)
                                                    ) : (
                                                        renderNormalLayout(formRenderProps)
                                                    )
                                                }
                                            </FormElement>
                                        )}
                                    />
                                </TabStripTab>
                                <TabStripTab disabled={mode === "add" ? true : false} title="Addresses">
                                    <Form
                                        // initialValues={{ description: formState.description }}
                                        render={(formRenderProps) => (
                                            <FormElement style={{ background: "white", borderRadius: "10px", padding: '1rem', width: '100%', marginBottom: '5rem' }} >
                                                <fieldset className={'k-form-fieldset'} disabled={props.readOnly}>
                                                    <div >

                                                        {
                                                            formState.sites.length ? (
                                                                <div style={accountContainerStyle}>
                                                                    {
                                                                        formState.sites.map((address, index) => (
                                                                            formState.primarySite.siteId !== address.siteId && (
                                                                                <>
                                                                                    <div style={accountStyle} key={address.siteId}>
                                                                                        <h5 style={titleStyle}>
                                                                                            <span>Address {index + 1}</span>
                                                                                            {!props.readOnly && <FaTrash style={{ cursor: "pointer" }} onClick={() => {
                                                                                                deleteAddressHandler(address.siteId)
                                                                                                setFormState((prevState) => ({
                                                                                                    ...prevState,
                                                                                                    sites: prevState.sites.filter((_, addrIndex) => addrIndex !== index)
                                                                                                }));
                                                                                            }} />}
                                                                                        </h5>
                                                                                        <Field
                                                                                            name={`addresses[${index}]`} // Tie the field to the correct index in the form state
                                                                                            component={FormAddressLookup}
                                                                                            data={addresses} // Pass dropdown data
                                                                                            setData={setAddresses} // Set dropdown data
                                                                                            label="Find address:"
                                                                                            selectedAddress={address} // Pass the current address object to the component
                                                                                            setSelectedAddress={(updatedAddress: any) => {
                                                                                                // Update only the relevant address in formState.addresses
                                                                                                setFormState((prevState) => ({
                                                                                                    ...prevState,
                                                                                                    sites: prevState.sites.map((addr, addrIndex) =>
                                                                                                        addrIndex === index ? { ...addr, ...updatedAddress } : addr
                                                                                                    ),
                                                                                                }));
                                                                                            }}
                                                                                            searchDropdown={(term: any) => searchAddresses(term, setAddresses)}
                                                                                            defaultAddress={address}
                                                                                            onChange={async (e) => {
                                                                                                // If value is null (user started typing), do nothing
                                                                                                if (!e.value) {
                                                                                                    return;
                                                                                                }
                                                                                                
                                                                                                const fullAddress = await getFullAddress(e.value.data);
                                                                                                // Update the full address when selected
                                                                                                setFormState((prevState) => ({
                                                                                                    ...prevState,
                                                                                                    sites: prevState.sites.map((addr, addrIndex) =>
                                                                                                        addrIndex === index ? { ...addr, ...fullAddress } : addr
                                                                                                    ),
                                                                                                }));
                                                                                            }}
                                                                                        />
                                                                                        <div className='button__container'>
                                                                                            {
                                                                                                props.mode === 'edit' && !props.readOnly && (
                                                                                                    <button
                                                                                                        disabled={isFormValid()}
                                                                                                        onClick={() => {
                                                                                                            address.isNewFromClient ? addNewAddressesToAccount(address.siteId) : editExistingAddress(address.siteId)
                                                                                                        }}
                                                                                                        className='form__edit__button accept'
                                                                                                    >
                                                                                                        {address.isNewFromClient ? 'Add Address' : 'Update Address'}
                                                                                                    </button>
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    </div>
                                                                                </>

                                                                            )
                                                                        ))
                                                                    }

                                                                </div>
                                                            ) : (
                                                                null
                                                            )
                                                        }
                                                        {!props.readOnly && (
                                                        <div onClick={() => addAdditionalAddressHandler()} style={{ marginTop: "20px", justifyContent: "center", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid #ccc', borderRadius: '5px' }}>
                                                            <FaPlusCircle size={20} /> <span>Add Address</span>
                                                        </div>
                                                        )}
                                                    </div>
                                                </fieldset>
                                            </FormElement>
                                        )}
                                    />

                                </TabStripTab>
                                <TabStripTab disabled={mode === "add" ? true : false} title="Contacts">
                                    <Form
                                        // initialValues={{ description: formState.description }}
                                        render={(formRenderProps) => (
                                            <FormElement style={{ background: "white", borderRadius: "10px", padding: '1rem', width: '100%', marginBottom: '5rem' }} >
                                                <fieldset className={'k-form-fieldset'} disabled={props.readOnly}>
                                                    <div >

                                                        {
                                                            formState.contacts.length ? (
                                                                <div style={accountContainerStyle}>

                                                                    {
                                                                        formState.contacts.map((contact, index) => (
                                                                            formState.primaryContact.contactId !== contact.contactId && (
                                                                                <div style={accountStyle} key={contact.contactId}>
                                                                                    <h5 style={titleStyle}><span>Contact {index + 1}</span>{!props.readOnly && <FaTrash style={{ cursor: "pointer" }} onClick={() => {
                                                                                        deleteContactHandler(contact.contactId)
                                                                                        setFormState((prevState) => ({
                                                                                            ...prevState,
                                                                                            contacts: prevState.contacts.filter((_, cont) => cont !== index)
                                                                                        }));
                                                                                    }} />}</h5>
                                                                                    <Field
                                                                                        data={titles}
                                                                                        name={`contacts[${index}].title`}
                                                                                        component={FormDropDownList}
                                                                                        label={'Title'}
                                                                                        defaultItem={{ text: contact.title, value: contact.titleId }}
                                                                                        onChange={(e) => {
                                                                                            setFormState(prev => {
                                                                                                const updatedContacts = [...prev.contacts];
                                                                                                updatedContacts[index] = { ...updatedContacts[index], title: e.value.text, titleId: e.value.value };
                                                                                                return {
                                                                                                    ...prev,
                                                                                                    contacts: updatedContacts
                                                                                                };
                                                                                            });
                                                                                        }}
                                                                                    />
                                                                                    <Field
                                                                                        name={'firstName'}
                                                                                        required={true}
                                                                                        component={FormInput}
                                                                                        label={'First Name:'}
                                                                                        defaultValue={contact.firstname}
                                                                                        onChange={(e) => {
                                                                                            setFormState((prevState) => ({
                                                                                                ...prevState,
                                                                                                contacts: prevState.contacts.map((cont, contIndex) =>
                                                                                                    contIndex === index ? { ...cont, firstname: e.value } : cont
                                                                                                ),
                                                                                            }));
                                                                                        }}
                                                                                    />
                                                                                    <Field
                                                                                        name={'lastName'}
                                                                                        required={true}
                                                                                        component={FormInput}
                                                                                        label={'Last Name:'}
                                                                                        defaultValue={contact.lastname}
                                                                                        onChange={(e) => {
                                                                                            setFormState((prevState) => ({
                                                                                                ...prevState,
                                                                                                contacts: prevState.contacts.map((cont, contIndex) =>
                                                                                                    contIndex === index ? { ...cont, lastname: e.value } : cont
                                                                                                ),
                                                                                            }));
                                                                                        }}
                                                                                    />
                                                                                    <Field
                                                                                        name={'email'}
                                                                                        required={true}
                                                                                        component={FormInput}
                                                                                        label={'Email:'}
                                                                                        type={'email'}
                                                                                        defaultValue={contact.email}
                                                                                        onChange={(e) => {
                                                                                            setFormState((prevState) => ({
                                                                                                ...prevState,
                                                                                                contacts: prevState.contacts.map((cont, contIndex) =>
                                                                                                    contIndex === index ? { ...cont, email: e.value } : cont
                                                                                                ),
                                                                                            }));
                                                                                        }}
                                                                                    />
                                                                                    <Field
                                                                                        name={'phone'}
                                                                                        required={true}
                                                                                        component={FormInput}
                                                                                        label={'Phone:'}
                                                                                        type={'tel'}
                                                                                        defaultValue={contact.phone}
                                                                                        onChange={(e) => {
                                                                                            setFormState((prevState) => ({
                                                                                                ...prevState,
                                                                                                contacts: prevState.contacts.map((cont, contIndex) =>
                                                                                                    contIndex === index ? { ...cont, phone: e.value } : cont
                                                                                                ),
                                                                                            }));
                                                                                        }}
                                                                                    />
                                                                                    <div className='button__container'>
                                                                                        {
                                                                                            props.mode === 'edit' && !props.readOnly && (
                                                                                                <button
                                                                                                    disabled={isFormValid()}
                                                                                                    onClick={() => {
                                                                                                        contact.isNewFromClient ? addNewContact(contact.contactId) : editExistingContact(contact.contactId)
                                                                                                    }}
                                                                                                    className='form__edit__button accept'
                                                                                                >
                                                                                                    {contact.isNewFromClient ? 'Add Contact' : 'Update Contact'}
                                                                                                </button>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>

                                                                            )
                                                                        ))
                                                                    }

                                                                </div>
                                                            ) : (
                                                                null
                                                            )
                                                        }
                                                        {!props.readOnly && (
                                                        <div onClick={() => addAdditionalContactHandler()} style={{ marginTop: "20px", justifyContent: "center", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid #ccc', borderRadius: '5px' }}>
                                                            <FaPlusCircle size={20} /> <span>Add Contact</span>
                                                        </div>
                                                        )}
                                                    </div>
                                                </fieldset>
                                            </FormElement>
                                        )}
                                    />
                                </TabStripTab>
                                <TabStripTab disabled={mode === "add" ? true : false} title="Files">
                                    {!props.readOnly && (
                                    <>
                                    <ExternalDropZone
                                        style={{ fontSize: '10pt', fontFamily: 'Heebo, sans-serif' }}
                                        uploadRef={uploadRef}
                                    />
                                    <div style={{ height: '20px' }} />
                                    <Upload
                                        ref={uploadRef}
                                        batch={true}
                                        files={files}
                                        multiple={true}
                                        defaultFiles={[]}
                                        withCredentials={false}
                                        listItemUI={CustomListItemUI}
                                        saveHeaders={{ Authorization: `Bearer ${token}` }}
                                        saveUrl={token ? `https://${apiURL}/Files/Client/${formState.clientId}` : ''}
                                        onStatusChange={handleUploadSuccess}
                                    />
                                    </>
                                    )}

                                    <div style={{ height: '1.5rem' }} />

                                    <div>
                                        {documentData.length > 0 ? (
                                            <div>
                                                {documentData.map((document: fileDocumentTypes) => (
                                                    <QuoteDocumentRow
                                                        key={document.documentId}
                                                        onDocumentId={document.documentId}
                                                        onFileName={document.fileName}
                                                        onFileExtension={document.fileExtension}
                                                        onDocumentDelete={handleDocumentDelete}
                                                        canDelete={!props.readOnly}
                                                        showVisibilityToggle={props.readOnly ? false : undefined}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    width: '100%',
                                                    padding: '1rem',
                                                    backgroundColor: 'rgb(216 229 237)',
                                                    borderRadius: '0.5rem',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <p style={{ margin: 0 }}>No files uploaded yet</p>
                                            </div>
                                        )}
                                    </div>
                                </TabStripTab>
                                <TabStripTab disabled={mode === "add" ? true : false} title="Notes">
                                    <div className='account-notes__wrapper'>
                                        <FormElement className='account-notes__form' style={{ background: 'white', borderRadius: '10px', padding: '1rem', width: '100%' }}>
                                            <fieldset className='k-form-fieldset account-notes__fieldset'>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, minHeight:0 }}>
                                                    <label htmlFor="client-notes" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Client Notes:</label>
                                                    <div className='account-notes__textarea-container'>
                                                        <textarea
                                                            id="client-notes"
                                                            value={formState.notes || ''}
                                                            onChange={e => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                                                            style={{ padding: '1rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '1rem' }}
                                                            placeholder="Enter notes about this client..."
                                                            disabled={mode === 'add' || props.readOnly}
                                                        />
                                                    </div>
                                                    {mode === 'edit' && !props.readOnly && (
                                                        <div className='button__container' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                            <button
                                                                onClick={() => onSubmitHandler()}
                                                                className='form__edit__button accept'
                                                                style={{ marginTop: '0.25rem' }}
                                                            >
                                                                Save Notes
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </fieldset>
                                        </FormElement>
                                    </div>
                                </TabStripTab>
                            </TabStrip>
                        </div>
                        {/* {
                            completeDialogVisible && (
                                <Dialog title={"Please confirm"} onClose={() => setCompleteDialogVisible(false)}>
                                    <p
                                        style={{
                                            margin: "25px",
                                            textAlign: "center",
                                        }}
                                    >
                                        Do you want to create a new Service Reminder for this client, site and customer, one year from the current date ({dayjs().add(1, 'year').format('DD-MM-YYYY')}?)
                                    </p>
                                    <DialogActionsBar>
                                        <button
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                            onClick={() => {
                                                setCompleteDialogVisible(false)
                                                completeClickHander(false)
                                                props.handleExpanded()
                                            }}
                                        >
                                            No, just mark as complete
                                        </button>
                                        <button
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                            onClick={() => {
                                                completeClickHander(true)
                                                handleExpanded()
                                            }}
                                        >
                                            Yes and mark as complete
                                        </button>
                                    </DialogActionsBar>
                                </Dialog>
                            )
                        } */}
                    </div>
                )
            }
        </>
    )
}

export default AccountForm